import React, { useState, useRef, useEffect } from 'react';
import { Triangle, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getChatResponse } from '../services/geminiService'; // ИСПРАВЛЕН ИМПОРТ
import { ChatMessage } from '../types';
import { TriquetraIcon } from './icons/TriquetraIcon';
import { lockScroll, unlockScroll } from '../services/scrollLock';

export const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: '0', role: 'model', text: 'Greetings. I am the digital twin of Helenendorfsky. How may I assist you today?', timestamp: Date.now() }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mobile-only launcher reveal: keep the widget hidden until the first scroll,
  // then fade it in (desktop is always visible via md: classes below). Flip once
  // on the first scroll, then drop the listener.
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // Listen for global modal events to hide/show the chat widget
  useEffect(() => {
    const handleModalOpen = () => setIsHidden(true);
    const handleModalClose = () => setIsHidden(false);

    window.addEventListener('modal-opened', handleModalOpen);
    window.addEventListener('modal-closed', handleModalClose);

    return () => {
      window.removeEventListener('modal-opened', handleModalOpen);
      window.removeEventListener('modal-closed', handleModalClose);
    };
  }, []);

  // Lock the background scroll while the chat is open ON MOBILE ONLY. On desktop the
  // chat is a small panel docked bottom-right and the page is meant to keep scrolling
  // behind it (and Lenis stays live), so we must NOT lock there — only below md, where
  // the chat becomes a full-height sheet. We reuse the same lockScroll/unlockScroll
  // service the modals use, so it cooperates with the scrollLock->Lenis wiring
  // (lockScroll stops Lenis; on mobile getLenis() is null so it's a no-op there and the
  // fixed-body lock does the work). A matchMedia listener re-syncs if the viewport
  // crosses the md breakpoint while open, and the effect always balances its lock on
  // cleanup so lockCount never leaks.
  useEffect(() => {
    if (!isOpen) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    let locked = false;
    const sync = () => {
      if (mq.matches && !locked) {
        lockScroll();
        locked = true;
      } else if (!mq.matches && locked) {
        unlockScroll();
        locked = false;
      }
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
      if (locked) unlockScroll();
    };
  }, [isOpen]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: input,
      timestamp: Date.now()
    };

    // Include the just-sent user message as the final turn of the history we
    // forward (state updates are async, so build the array explicitly here).
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    // Вызываем нашу новую функцию (с историей разговора для контекста)
    const responseText = await getChatResponse(input, updatedMessages);

    const botMsg: ChatMessage = {
      id: (Date.now() + 1).toString(),
      role: 'model',
      text: responseText,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, botMsg]);
    setIsLoading(false);
  };

  if (isHidden) return null;

  return (
    <div
      className={`
        fixed bottom-3 right-6 z-50 flex flex-col items-end transition-opacity duration-300
        md:bottom-6 md:opacity-100 md:pointer-events-auto
        ${hasScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}
      `}
    >
      <AnimatePresence>
        {isOpen && (
        <motion.div
          key="chat-panel"
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="
          fixed inset-x-2 bottom-[calc(0.5rem+env(safe-area-inset-bottom))] top-28
          md:static md:translate-x-0 md:w-96 md:mb-4 md:max-h-none md:h-[500px]
          bg-white/80 dark:bg-zinc-900/80 [.nardogrey_&]:bg-[#686A6C]/90 backdrop-blur-2xl
          border border-white/40 dark:border-white/10 rounded-3xl
          shadow-xl md:shadow-2xl overflow-hidden flex flex-col
          origin-bottom-right
        ">
          {/* Header */}
          <div className="p-4 border-b border-black/5 dark:border-white/5 bg-white/40 dark:bg-white/5 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 [.nardogrey_&]:from-zinc-100 [.nardogrey_&]:to-zinc-300 flex items-center justify-center">
                <Triangle size={16} className="text-white [.nardogrey_&]:text-black" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Digital Twin</h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Powered by Gemini 2.5</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
              className="p-2 -mr-2 rounded-full text-zinc-700 hover:bg-black/5 hover:text-zinc-900 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white [.nardogrey_&]:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages — data-lenis-prevent so wheeling the chat scrolls the list,
              not the page behind it (the chat is not a modal, Lenis stays active). */}
          <div data-lenis-prevent className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide" ref={scrollRef}>
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${msg.role === 'model'
                    ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-white'
                    : 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'}
                `}>
                  {msg.role === 'model' ? <TriquetraIcon size={16} /> : <User size={16} />}
                </div>
                <div className={`
                  p-3 rounded-2xl max-w-[80%] text-sm leading-relaxed
                  ${msg.role === 'model'
                    ? 'bg-white/60 text-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-200 rounded-tl-none border border-black/5 dark:border-transparent'
                    : 'bg-zinc-900/90 text-white dark:bg-white/10 dark:text-white rounded-tr-none backdrop-blur-sm'}
                `}>
                  {msg.text || msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-3">
                 <div className="w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-zinc-900 dark:text-white">
                  <TriquetraIcon size={16} />
                </div>
                <div className="bg-white/60 dark:bg-zinc-800/50 p-3 rounded-2xl rounded-tl-none flex gap-1 items-center">
                  <div className="w-2 h-2 bg-zinc-400 dark:bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}/>
                  <div className="w-2 h-2 bg-zinc-400 dark:bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}/>
                  <div className="w-2 h-2 bg-zinc-400 dark:bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}/>
                </div>
              </div>
            )}
          </div>

          {/* Input — the panel's own `bottom` now clears env(safe-area-inset-bottom)
              (see className above), so p-4's 1rem bottom padding is enough; adding the
              safe-area inset again here would double-count and float the field too high. */}
          <form onSubmit={handleSend} className="p-4 border-t border-black/5 dark:border-white/5 bg-zinc-50/50 dark:bg-zinc-900/50">
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about my projects..."
                className="
                  w-full pl-4 pr-12 py-3 text-base md:text-sm rounded-full transition-colors focus:outline-none focus:border-blue-500/50
                  bg-white border border-zinc-200 text-zinc-900
                  dark:bg-zinc-950/50 dark:border-zinc-800 dark:text-white
                "
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="absolute right-2 top-2 p-1.5 bg-white border border-zinc-300 rounded-full text-black disabled:opacity-50 hover:bg-zinc-200 transition-colors"
              >
                <Triangle size={16} fill="currentColor" />
              </button>
            </div>
          </form>
        </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          group items-center justify-center w-14 h-14
          backdrop-blur-xl rounded-full shadow-md md:shadow-lg hover:scale-110
          transition-all duration-300
          bg-white/80 border border-white/60 text-zinc-700 hover:bg-white
          dark:bg-white/5 dark:border-white/10 dark:text-white dark:hover:bg-white/10
          ${isOpen ? 'hidden md:flex' : 'flex'}
        `}
      >
        <Triangle className="transition-colors" />
      </button>
    </div>
  );
};
